import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import Swal from "sweetalert2";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 750,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
  maxHeight: "92vh",
  overflowY: "auto",
};

const ACTIVITY_TYPES = [
  { value: "gang_create", label: "Create a Gang", defaultUnit: "per_action" },
  {
    value: "gang_duration",
    label: "Stay in Gang (Duration)",
    defaultUnit: "per_hour",
  },
  {
    value: "individual_game",
    label: "Individual Games",
    defaultUnit: "per_action",
  },
  {
    value: "gift_collected",
    label: "Gifts Collected",
    defaultUnit: "per_gift",
  },
  { value: "gift_sent", label: "Gifts Sent", defaultUnit: "per_gift" },
  { value: "audio_call", label: "Audio Calls", defaultUnit: "per_minute" },
  { value: "video_call", label: "Video Calls", defaultUnit: "per_minute" },
  { value: "daily_login", label: "Daily Login", defaultUnit: "per_action" },
];

const UNIT_OPTIONS = [
  { value: "per_action", label: "Per Action" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_minute", label: "Per Minute" },
  { value: "per_gift", label: "Per Gift" },
];

const REWARD_TYPES = [
  { value: "coins", label: "Coins" },
  { value: "badge", label: "Badge" },
  { value: "gift", label: "Gift" },
];

const defaultFormData = {
  name: "",
  description: "",
  bannerImage: "",
  startDate: "",
  endDate: "",
  status: "draft",
  pointCriteria: [
    {
      activityType: "gang_create",
      pointsPerUnit: 50,
      unit: "per_action",
      description: "Create a gang to earn points",
      isActive: true,
    },
    {
      activityType: "gang_duration",
      pointsPerUnit: 10,
      unit: "per_hour",
      description: "Stay active in a gang",
      isActive: true,
    },
    {
      activityType: "individual_game",
      pointsPerUnit: 20,
      unit: "per_action",
      description: "Play individual games",
      isActive: true,
    },
    {
      activityType: "gift_collected",
      pointsPerUnit: 5,
      unit: "per_gift",
      description: "Collect gifts from others",
      isActive: true,
    },
    {
      activityType: "gift_sent",
      pointsPerUnit: 10,
      unit: "per_gift",
      description: "Send gifts to others",
      isActive: true,
    },
    {
      activityType: "daily_login",
      pointsPerUnit: 15,
      unit: "per_action",
      description: "Login daily during event",
      isActive: true,
    },
  ],
  peakHours: [],
  rewards: [
    { rank: 1, rewardType: "coins", rewardValue: 1000, description: "1st Place" },
    { rank: 2, rewardType: "coins", rewardValue: 500, description: "2nd Place" },
    { rank: 3, rewardType: "coins", rewardValue: 250, description: "3rd Place" },
  ],
};

const CreateEditEventModal = ({
  open,
  handleClose,
  fetchEvents,
  editData = null,
}) => {
  const isEditMode = Boolean(editData);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [activeTab, setActiveTab] = useState("basic"); // basic | criteria | peakhours | rewards

  useEffect(() => {
    if (editData) {
      setFormData({
        ...editData,
        startDate: editData.startDate
          ? new Date(editData.startDate).toISOString().substring(0, 10)
          : "",
        endDate: editData.endDate
          ? new Date(editData.endDate).toISOString().substring(0, 10)
          : "",
      });
    } else {
      setFormData({ ...defaultFormData });
    }
  }, [editData, open]);

  // ── Generic field handler ──
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ── Point Criteria handlers ──
  const handleCriteriaChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.pointCriteria];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, pointCriteria: updated };
    });
  };

  const addCriteria = () => {
    setFormData((prev) => ({
      ...prev,
      pointCriteria: [
        ...prev.pointCriteria,
        {
          activityType: "audio_call",
          pointsPerUnit: 5,
          unit: "per_minute",
          description: "",
          isActive: true,
        },
      ],
    }));
  };

  const removeCriteria = (index) => {
    setFormData((prev) => ({
      ...prev,
      pointCriteria: prev.pointCriteria.filter((_, i) => i !== index),
    }));
  };

  // ── Peak Hours handlers ──
  const handlePeakHourChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.peakHours];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, peakHours: updated };
    });
  };

  const addPeakHour = () => {
    setFormData((prev) => ({
      ...prev,
      peakHours: [
        ...prev.peakHours,
        {
          startTime: "18:00",
          endTime: "22:00",
          multiplier: 2,
          label: "Peak Hour",
          isActive: true,
        },
      ],
    }));
  };

  const removePeakHour = (index) => {
    setFormData((prev) => ({
      ...prev,
      peakHours: prev.peakHours.filter((_, i) => i !== index),
    }));
  };

  // ── Rewards handlers ──
  const handleRewardChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.rewards];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, rewards: updated };
    });
  };

  const addReward = () => {
    const nextRank =
      formData.rewards.length > 0
        ? Math.max(...formData.rewards.map((r) => r.rank)) + 1
        : 1;
    setFormData((prev) => ({
      ...prev,
      rewards: [
        ...prev.rewards,
        {
          rank: nextRank,
          rewardType: "coins",
          rewardValue: 100,
          description: `Rank #${nextRank}`,
        },
      ],
    }));
  };

  const removeReward = (index) => {
    setFormData((prev) => ({
      ...prev,
      rewards: prev.rewards.filter((_, i) => i !== index),
    }));
  };

  // ── Validate ──
  const validateForm = () => {
    if (!formData.name?.trim()) return "Event name is required.";
    if (!formData.startDate) return "Start date is required.";
    if (!formData.endDate) return "End date is required.";
    if (new Date(formData.startDate) >= new Date(formData.endDate))
      return "End date must be after start date.";
    if (formData.pointCriteria.length === 0)
      return "At least one point criteria is required.";
    for (const c of formData.pointCriteria) {
      if (!c.pointsPerUnit || c.pointsPerUnit <= 0)
        return `Points for ${c.activityType} must be > 0.`;
    }
    for (const ph of formData.peakHours) {
      if (!ph.startTime || !ph.endTime)
        return "Peak hour start/end times are required.";
      if (ph.multiplier < 1) return "Peak hour multiplier must be >= 1.";
    }
    return null;
  };

  // ── Submit ──
  const handleSubmit = () => {
    const error = validateForm();
    if (error) {
      Swal.fire({ icon: "error", title: "Validation Error", text: error });
      return;
    }

    setIsLoading(true);

    const payload = {
      ...formData,
      // Send dates with IST offset so the server stores correct Indian time
      startDate: formData.startDate
        ? new Date(formData.startDate + "T00:00:00+05:30").toISOString()
        : "",
      endDate: formData.endDate
        ? new Date(formData.endDate + "T23:59:59+05:30").toISOString()
        : "",
    };

    // Remove mongo fields if editing
    delete payload._id;
    delete payload.__v;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.totalParticipants;

    const request = isEditMode
      ? axios.put(
          `${process.env.REACT_APP_API_URL}/event/update/${editData._id}`,
          payload
        )
      : axios.post(
          `${process.env.REACT_APP_API_URL}/event/create`,
          payload
        );

    request
      .then(() => {
        Swal.fire({
          icon: "success",
          title: isEditMode ? "Event Updated!" : "Event Created!",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchEvents();
        handleClose();
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Something went wrong.",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const tabs = [
    { key: "basic", label: "📋 Basic Info" },
    { key: "criteria", label: "🎯 Point Criteria" },
    { key: "peakhours", label: "⚡ Peak Hours" },
    { key: "rewards", label: "🏆 Rewards" },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {isEditMode ? "✏️ Edit Event" : "🎉 Create New Event"}
          </h2>
          <IconButton onClick={handleClose}>
            <FaTimes />
          </IconButton>
        </div>

        {/* Tab Navigation */}
        <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-all ${
                activeTab === tab.key
                  ? "bg-[#c51369] text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ─── TAB: Basic Info ─── */}
        {activeTab === "basic" && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Event Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Dil Se Dil Tak Event"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Describe the event..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Banner Image URL
              </label>
              <input
                type="text"
                name="bannerImage"
                value={formData.bannerImage}
                onChange={handleChange}
                placeholder="https://res.cloudinary.com/..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              />
              {formData.bannerImage && (
                <img
                  src={formData.bannerImage}
                  alt="Banner preview"
                  className="mt-2 h-24 w-full rounded-lg object-cover"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  End Date *
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#c51369] focus:outline-none focus:ring-1 focus:ring-[#c51369]"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        )}

        {/* ─── TAB: Point Criteria ─── */}
        {activeTab === "criteria" && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Configure which activities earn points and how much each is worth.
            </p>

            {formData.pointCriteria.map((criteria, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-200 bg-gray-50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    Activity #{index + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={criteria.isActive}
                        onChange={(e) =>
                          handleCriteriaChange(
                            index,
                            "isActive",
                            e.target.checked
                          )
                        }
                      />
                      Active
                    </label>
                    <IconButton
                      size="small"
                      onClick={() => removeCriteria(index)}
                    >
                      <FaTrash className="text-xs text-red-400" />
                    </IconButton>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">
                      Activity Type
                    </label>
                    <select
                      value={criteria.activityType}
                      onChange={(e) =>
                        handleCriteriaChange(
                          index,
                          "activityType",
                          e.target.value
                        )
                      }
                      className="w-full rounded border px-2 py-1 text-sm"
                    >
                      {ACTIVITY_TYPES.map((at) => (
                        <option key={at.value} value={at.value}>
                          {at.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">
                      Points Per Unit
                    </label>
                    <input
                      type="number"
                      value={criteria.pointsPerUnit}
                      onChange={(e) =>
                        handleCriteriaChange(
                          index,
                          "pointsPerUnit",
                          Number(e.target.value)
                        )
                      }
                      min="0"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Unit</label>
                    <select
                      value={criteria.unit}
                      onChange={(e) =>
                        handleCriteriaChange(index, "unit", e.target.value)
                      }
                      className="w-full rounded border px-2 py-1 text-sm"
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">
                      Description
                    </label>
                    <input
                      type="text"
                      value={criteria.description}
                      onChange={(e) =>
                        handleCriteriaChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      placeholder="Short description"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addCriteria}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-2 text-sm text-gray-500 hover:border-[#c51369] hover:text-[#c51369]"
            >
              <FaPlus className="text-xs" /> Add Activity
            </button>
          </div>
        )}

        {/* ─── TAB: Peak Hours ─── */}
        {activeTab === "peakhours" && (
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-xs text-amber-700">
                ⚡ <strong>Peak Hours</strong> multiply all points earned during
                the specified time window. For example, a 2x multiplier doubles
                points earned between the start and end time.
              </p>
            </div>

            {formData.peakHours.length === 0 && (
              <p className="py-4 text-center text-sm text-gray-400">
                No peak hours configured. Click below to add one.
              </p>
            )}

            {formData.peakHours.map((ph, index) => (
              <div
                key={index}
                className="rounded-lg border border-amber-200 bg-amber-50/50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    ⚡ Peak Hour #{index + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={ph.isActive}
                        onChange={(e) =>
                          handlePeakHourChange(
                            index,
                            "isActive",
                            e.target.checked
                          )
                        }
                      />
                      Active
                    </label>
                    <IconButton
                      size="small"
                      onClick={() => removePeakHour(index)}
                    >
                      <FaTrash className="text-xs text-red-400" />
                    </IconButton>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={ph.startTime}
                      onChange={(e) =>
                        handlePeakHourChange(
                          index,
                          "startTime",
                          e.target.value
                        )
                      }
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">End Time</label>
                    <input
                      type="time"
                      value={ph.endTime}
                      onChange={(e) =>
                        handlePeakHourChange(
                          index,
                          "endTime",
                          e.target.value
                        )
                      }
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">
                      Multiplier (e.g., 2 = 2x points)
                    </label>
                    <input
                      type="number"
                      value={ph.multiplier}
                      onChange={(e) =>
                        handlePeakHourChange(
                          index,
                          "multiplier",
                          Number(e.target.value)
                        )
                      }
                      min="1"
                      step="0.5"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Label</label>
                    <input
                      type="text"
                      value={ph.label}
                      onChange={(e) =>
                        handlePeakHourChange(index, "label", e.target.value)
                      }
                      placeholder="e.g., Evening Rush"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addPeakHour}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 py-2 text-sm text-amber-600 hover:border-amber-500 hover:text-amber-700"
            >
              <FaPlus className="text-xs" /> Add Peak Hour
            </button>
          </div>
        )}

        {/* ─── TAB: Rewards ─── */}
        {activeTab === "rewards" && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Configure rewards for top-ranking participants. Rewards are
              distributed after the event is completed.
            </p>

            {formData.rewards.map((reward, index) => (
              <div
                key={index}
                className="rounded-lg border border-yellow-200 bg-yellow-50/50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">
                    🏆 Rank #{reward.rank}
                  </span>
                  <IconButton
                    size="small"
                    onClick={() => removeReward(index)}
                  >
                    <FaTrash className="text-xs text-red-400" />
                  </IconButton>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Rank</label>
                    <input
                      type="number"
                      value={reward.rank}
                      onChange={(e) =>
                        handleRewardChange(
                          index,
                          "rank",
                          Number(e.target.value)
                        )
                      }
                      min="1"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">
                      Reward Type
                    </label>
                    <select
                      value={reward.rewardType}
                      onChange={(e) =>
                        handleRewardChange(
                          index,
                          "rewardType",
                          e.target.value
                        )
                      }
                      className="w-full rounded border px-2 py-1 text-sm"
                    >
                      {REWARD_TYPES.map((rt) => (
                        <option key={rt.value} value={rt.value}>
                          {rt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">
                      Reward Value
                    </label>
                    <input
                      type="number"
                      value={reward.rewardValue}
                      onChange={(e) =>
                        handleRewardChange(
                          index,
                          "rewardValue",
                          Number(e.target.value)
                        )
                      }
                      min="0"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">
                      Description
                    </label>
                    <input
                      type="text"
                      value={reward.description}
                      onChange={(e) =>
                        handleRewardChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      placeholder="e.g., Gold Winner"
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addReward}
              className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-yellow-300 py-2 text-sm text-yellow-600 hover:border-yellow-500 hover:text-yellow-700"
            >
              <FaPlus className="text-xs" /> Add Reward Tier
            </button>
          </div>
        )}

        {/* ─── Submit Button ─── */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300"
          >
            Cancel
          </button>

          {isLoading ? (
            <button className="rounded-lg bg-[#c51369] px-6 py-2 text-sm text-white">
              <BtnLoader />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="rounded-lg bg-[#c51369] px-6 py-2 text-sm font-medium text-white hover:bg-[#a10f5b]"
            >
              {isEditMode ? "Update Event" : "Create Event"}
            </button>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default CreateEditEventModal;
